import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { labelFor, colorFor, BookingStatusLegend } from "@/lib/bookingStatus";
import {
  Users, Search, Mail, Phone, MapPin, Eye, Star, BookOpenCheck,
  CalendarRange, Wallet, Moon, Sparkles, Save, BadgePercent,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/dates";
import { buildWhatsAppLink, waMessageForCustomer } from "@/lib/whatsapp";
import { MessageCircle } from "lucide-react";
import { errMsg } from "@/lib/errors";

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("spent_desc");
  const [filterReturning, setFilterReturning] = useState("all");
  const [detail, setDetail] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers");
      setCustomers(r.data.customers || []);
    } catch {
      toast.error("Errore caricamento rubrica");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const list = useMemo(() => {
    let arr = [...customers];
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter((c) =>
        (c.email || "").toLowerCase().includes(s) ||
        `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(s) ||
        (c.phone || "").toLowerCase().includes(s) ||
        (c.city || "").toLowerCase().includes(s),
      );
    }
    if (filterReturning === "returning") arr = arr.filter((c) => c.is_returning);
    if (filterReturning === "once") arr = arr.filter((c) => !c.is_returning);
    if (sortBy === "spent_desc") arr.sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0));
    else if (sortBy === "bookings_desc") arr.sort((a, b) => (b.total_bookings || 0) - (a.total_bookings || 0));
    else if (sortBy === "last_stay_desc") arr.sort((a, b) => (b.last_stay || "").localeCompare(a.last_stay || ""));
    else if (sortBy === "name_asc") arr.sort((a, b) => `${a.last_name || ""}`.localeCompare(`${b.last_name || ""}`));
    return arr;
  }, [customers, search, sortBy, filterReturning]);

  const openDetail = async (customer) => {
    setDetail({ loading: true, customer });
    try {
      const [r, p] = await Promise.all([
        api.get(`/admin/customers/${encodeURIComponent(customer.email)}`),
        api.get(`/admin/customer-preferences/${encodeURIComponent(customer.email)}`).catch(() => ({ data: {} })),
      ]);
      setDetail({ loading: false, customer, ...r.data, prefs: p.data });
    } catch (e) {
      toast.error(errMsg(e, "Errore caricamento dettaglio"));
      setDetail(null);
    }
  };

  const savePrefs = async (email, prefs) => {
    try {
      await api.put(`/admin/customer-preferences/${encodeURIComponent(email)}`, {
        custom_discount_pct: prefs.custom_discount_pct ? Number(prefs.custom_discount_pct) : null,
        custom_nightly_rate: prefs.custom_nightly_rate ? Number(prefs.custom_nightly_rate) : null,
        private_notes: prefs.private_notes || "",
        tags: prefs.tags || [],
      });
      toast.success("Preferenze salvate · valide per le prossime prenotazioni");
      setDetail((d) => ({ ...d, prefs }));
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    }
  };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
          <Users className="w-5 h-5" /> Rubrica Clienti
        </h3>
        <p className="text-xs text-[#635F59] mt-1">
          Aggregato unico per email di tutti i clienti che hanno fatto almeno una richiesta.
          Le badge "Habitué" identificano i clienti con ≥2 prenotazioni confermate (sconto fedeltà già attivo automaticamente).
        </p>
      </div>

      {/* FILTERS */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#635F59]" />
          <Input placeholder="Cerca per nome, email, telefono o città..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cust-search" className="pl-9 h-10" />
        </div>
        <div className="md:col-span-3">
          <Select value={filterReturning} onValueChange={setFilterReturning}>
            <SelectTrigger className="h-10" data-testid="cust-filter-returning"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              <SelectItem value="returning">Solo habitué (≥2)</SelectItem>
              <SelectItem value="once">Solo prima visita</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10" data-testid="cust-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="spent_desc">Spesa totale ↓</SelectItem>
              <SelectItem value="bookings_desc">Più prenotazioni ↓</SelectItem>
              <SelectItem value="last_stay_desc">Ultimo soggiorno ↓</SelectItem>
              <SelectItem value="name_asc">Cognome A-Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1 text-right">
          <Badge className="bg-[#1E3A5F] text-white">{list.length}</Badge>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#635F59]">Caricamento…</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-[#635F59]" data-testid="cust-empty">Nessun cliente.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="cust-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead>Località</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Prenotaz.</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Notti</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Speso</TableHead>
                  <TableHead className="whitespace-nowrap">Ultimo soggiorno</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id} data-testid={`cust-row-${c.id}`}>
                    <TableCell className="whitespace-nowrap">
                      <div className="font-medium text-[#1E3A5F] flex items-center gap-2">
                        {c.first_name} {c.last_name}
                        {c.is_returning && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" title="Habitué" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{c.email}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{c.phone || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{[c.city, c.country].filter(Boolean).join(", ") || "—"}</TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      <Badge variant="outline" className="border-[#E8E2D9]">
                        {c.confirmed_bookings}/{c.total_bookings}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap tabular-nums">{c.total_nights || 0}</TableCell>
                    <TableCell className="text-right font-semibold text-[#AC5D45] whitespace-nowrap tabular-nums">€&nbsp;{(c.total_spent || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{c.last_stay ? formatDate(c.last_stay) : "—"}</TableCell>
                    <TableCell className="w-10">
                      <Button size="icon" variant="ghost" onClick={() => openDetail(c)} data-testid={`cust-view-${c.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cust-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {detail?.customer?.first_name} {detail?.customer?.last_name}
              {detail?.customer?.is_returning && <Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
            </DialogTitle>
          </DialogHeader>
          {detail?.loading && <div className="p-6 text-center text-[#635F59]">Caricamento…</div>}
          {detail && !detail.loading && detail.profile && (() => {
            // Pre-compute derived strings once when rendering this dialog body
            // (rather than inside JSX where they'd recreate small arrays each render)
            const provenienza = [detail.profile.city, detail.profile.country].filter(Boolean).join(", ");
            return (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Card icon={<BookOpenCheck className="w-4 h-4" />} label="Prenotazioni" value={detail.profile.total_bookings} />
                <Card icon={<Moon className="w-4 h-4" />} label="Notti totali" value={detail.profile.total_nights} />
                <Card icon={<Wallet className="w-4 h-4" />} label="Speso €" value={`€ ${detail.profile.total_spent.toFixed(2)}`} highlight />
                <Card icon={<CalendarRange className="w-4 h-4" />} label="Email" value={detail.profile.email} small />
              </div>

              <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
                {detail.profile.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{detail.profile.phone}</span>}
                {detail.profile.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{detail.profile.email}</span>}
                {provenienza && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{provenienza}</span>}
                {(() => {
                  const wa = buildWhatsAppLink(detail.profile.phone, waMessageForCustomer(detail.customer?.first_name));
                  if (!wa) return null;
                  return (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-1 h-7 px-2.5 rounded bg-[#25D366] hover:bg-[#1FBA56] text-white text-xs font-medium"
                      title={`Scrivi a ${detail.customer?.first_name || ""} su WhatsApp`}
                      data-testid="customer-whatsapp"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  );
                })()}
              </div>

              <PreferencesEditor
                email={detail.profile.email}
                initial={detail.prefs}
                onSave={savePrefs}
              />

              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h4 className="font-semibold text-[#1E3A5F]">Storico prenotazioni ({(detail.bookings || []).length})</h4>
                  <BookingStatusLegend />
                </div>
                <div className="rounded-lg border border-[#E8E2D9] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Codice</TableHead>
                        <TableHead>Camera</TableHead>
                        <TableHead>Date soggiorno</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Tassa</TableHead>
                        <TableHead className="text-right">Totale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.bookings || []).map((b) => (
                        <TableRow key={b.id}>
                          <TableCell className="font-mono text-xs">{b.code}</TableCell>
                          <TableCell className="text-xs">{b.room_name}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{formatDate(b.check_in)} → {formatDate(b.check_out)}</TableCell>
                          <TableCell><Badge variant="outline" className={colorFor(b.status)}>{labelFor(b.status)}</Badge></TableCell>
                          <TableCell>
                            {b.tassa_paid ? (
                              <Badge
                                variant="outline"
                                className="bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold whitespace-nowrap"
                                title={b.tassa_method ? `Pagata via ${b.tassa_method}${b.tassa_amount ? ` · €${Number(b.tassa_amount).toFixed(2)}` : ""}` : "Tassa di soggiorno pagata"}
                                data-testid={`cust-tassa-paid-${b.code}`}
                              >
                                ✅ Pagata
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-amber-50 text-amber-800 border-amber-300 font-semibold whitespace-nowrap"
                                title="Tassa di soggiorno non ancora pagata"
                                data-testid={`cust-tassa-unpaid-${b.code}`}
                              >
                                ⏳ Non pagata
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs">€ {b.total.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {detail.messages && detail.messages.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#1E3A5F] mb-2">Messaggi inviati ({detail.messages.length})</h4>
                  <div className="space-y-2">
                    {detail.messages.map((m) => (
                      <div key={m.id} className="text-xs bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-2">
                        <div className="font-semibold">{m.subject} <span className="text-[#635F59] font-normal">· {formatDateTime(m.created_at)}</span></div>
                        <div className="whitespace-pre-wrap mt-1">{m.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Card = ({ icon, label, value, highlight, small }) => (
  <div className={`border rounded-lg p-3 ${highlight ? "bg-[#AC5D45]/5 border-[#AC5D45]/30" : "bg-white border-[#E8E2D9]"}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#635F59]">{icon}{label}</div>
    <div className={`mt-1 font-display ${small ? "text-xs" : "text-xl"} ${highlight ? "text-[#AC5D45]" : "text-[#1E3A5F]"} ${small ? "truncate" : ""}`}>{value}</div>
  </div>
);

// ----- Preferences editor: discount / rate / private notes per customer -----
const PreferencesEditor = ({ email, initial, onSave }) => {
  const [prefs, setPrefs] = useState({
    custom_discount_pct: initial?.custom_discount_pct ?? "",
    custom_nightly_rate: initial?.custom_nightly_rate ?? "",
    private_notes: initial?.private_notes ?? "",
    tags: initial?.tags ?? [],
  });
  const [busy, setBusy] = useState(false);

  return (
    <div className="bg-[#FDF1E6] border border-[#AC5D45]/30 rounded-xl p-4" data-testid="pref-editor">
      <h4 className="font-semibold text-[#AC5D45] mb-2 flex items-center gap-1.5">
        <Sparkles className="w-4 h-4" /> Preferenze personalizzate
      </h4>
      <p className="text-[11px] text-[#635F59] mb-3">
        Questi valori saranno automaticamente suggeriti nello "Smart Suggest" del Calendario quando crei una nuova prenotazione per questo cliente.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs flex items-center gap-1"><BadgePercent className="w-3 h-3" />Sconto fisso %</Label>
          <Input
            type="number" step="0.1" min={0} max={100}
            value={prefs.custom_discount_pct}
            onChange={(e) => setPrefs({ ...prefs, custom_discount_pct: e.target.value })}
            placeholder="es. 10"
            data-testid="pref-discount"
          />
        </div>
        <div>
          <Label className="text-xs">Tariffa €/notte (override)</Label>
          <Input
            type="number" step="0.01" min={0}
            value={prefs.custom_nightly_rate}
            onChange={(e) => setPrefs({ ...prefs, custom_nightly_rate: e.target.value })}
            placeholder="es. 55"
            data-testid="pref-rate"
          />
        </div>
      </div>
      <div className="mt-2">
        <Label className="text-xs">Note private</Label>
        <Textarea
          rows={2}
          value={prefs.private_notes}
          onChange={(e) => setPrefs({ ...prefs, private_notes: e.target.value })}
          placeholder="Es. preferisce camera al primo piano, allergico alle piume, paga in contanti..."
          data-testid="pref-notes"
        />
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          size="sm"
          disabled={busy}
          onClick={async () => { setBusy(true); await onSave(email, prefs); setBusy(false); }}
          data-testid="pref-save"
          className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
        >
          <Save className="w-3.5 h-3.5 mr-1" />Salva preferenze
        </Button>
      </div>
    </div>
  );
};
